import { Typography } from '@mui/joy';
import { BarChart, HighlightScope } from '@mui/x-charts';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useSWR from 'swr';

import ReportGraphLegend from './reportGraphLegend';

import { convertHours } from '../../../assets/utils/helpers';
import {
  AverageHandlingTimeObject,
  BarChartObject,
  ReportBarChartProps,
  ReportByCaseManagerProps,
  ReportFilters,
  TotalReportsObject,
} from '../../../interfaces/ReportsByCaseManager';
import {
  getUrlString,
  useDashoardFilter,
} from '../../../Page/customer/customerDashboard';
import LoadingComponent from '../../common/loadingComponent';
import NoDataFound from '../../common/noDataFound';

import { Card } from '~/components/ui/card';
import { Option, Select } from '~/components/ui/select';
import { usePartnerContext } from '~/contexts/partner/partnerContext';
import { CheckMetricWrapper } from '~/pages/customer/dashboard/checkMetricWrapper';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';

const getCustomBarWidth = (labels: string[]) => {
  if (labels.length === 1) return 0.85;
  if (labels.length === 2) return 0.7;
  if (labels.length === 3) return 0.6;

  return 0.4;
};

const headerText = {
  fontSize: '18px',
  fontWeight: 500,
};

const ReportsBarGraph = ({
  reportsDataList,
  reportLabels,
  report,
  palette,
}: ReportBarChartProps) => {
  return (
    <BarChart
      layout='horizontal'
      colors={palette}
      axisHighlight={{ x: 'line', y: 'band' }}
      slotProps={{
        legend: {
          hidden: true,
          labelStyle: {
            fontSize: 12,
          },
        },
      }}
      height={330}
      margin={{
        top: 10,
        left: 120,
      }}
      series={reportsDataList.map((reportData) => ({
        ...reportData,
        valueFormatter: (value) => {
          if (report === ReportFilters.AVG_HANDLING_TIME) {
            return convertHours(value);
          } else {
            return `${value}`;
          }
        },
        highlightScope: {
          highlighted: 'series',
          faded: 'none',
        } as HighlightScope,
      }))}
      xAxis={[
        {
          tickMaxStep:
            report !== ReportFilters.AVG_HANDLING_TIME
              ? reportsDataList.reduce((max, item) => {
                  // This is to get the max value in the data array
                  const maxInItem = Math.max(...item.data);
                  return Math.max(max, maxInItem);
                }, -Infinity)
              : undefined,
          tickMinStep:
            report !== ReportFilters.AVG_HANDLING_TIME ? 1 : undefined,
          valueFormatter: (value) => {
            if (report === ReportFilters.AVG_HANDLING_TIME) {
              return convertHours(value);
            } else {
              return `${value}`;
            }
          },
        },
      ]}
      yAxis={[
        {
          scaleType: 'band',
          data: reportLabels,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          categoryGapRatio: getCustomBarWidth(reportLabels),
          barGapRatio: 5,
        },
      ]}
    />
  );
};

const transformReportDataToBarChart = (dataFromApi: TotalReportsObject[]) => {
  const labels: string[] = [];
  const legends: string[] = [];

  dataFromApi.forEach((item) => {
    legends.push(item.case_manager_name ?? 'No Name');
    item.reports.forEach(
      (report) => !labels.includes(report.status) && labels.push(report.status),
    );
  });

  const transformedData = labels.map((labelItem) => {
    return {
      fullLabel: labelItem,
      label:
        labelItem.length > 12 ? `${labelItem.substring(0, 12)}...` : labelItem,
      data: dataFromApi.map((item) => {
        const report = item?.reports?.find((reportItem) => {
          return reportItem.status === labelItem;
        });
        return report?.count ?? 0;
      }),
      stack: 'total',
    };
  });

  return {
    data: transformedData,
    legend: legends,
  };
};

function TotalReports({
  filterBy,
  date,
  dateEnd,
  tenantId,
}: Readonly<ReportByCaseManagerProps>) {
  const link = 'dashboard/reports-by-case-manager-and-status';
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const [reportsDataList, setReportsDataList] = useState<BarChartObject[]>([]);
  const [reportLabels, setReportLabels] = useState<string[]>([]);
  const palette = [
    '#007878',
    '#00CECF',
    '#2449FC',
    '#FFC700',
    '#03FF75',
    '#00692F',
    '#FF6A54',
  ];

  const { data, error, isLoading } = useSWR(
    getUrlString(link, filterBy, date, dateEnd, tenantId, selectedChannel),
    {
      revalidateOnFocus: true,
    },
  );

  useEffect(() => {
    if (!isLoading && !error && data) {
      const reportData = transformReportDataToBarChart(data);
      setReportsDataList(reportData.data);
      setReportLabels(reportData.legend);
    }
  }, [data, error, isLoading]);

  return (
    <>
      {isLoading && <LoadingComponent />}
      {error && (
        <Typography level='body-sm' color='danger'>
          {error.message}
        </Typography>
      )}
      {!isLoading &&
        !error &&
        reportsDataList.length !== 0 &&
        reportLabels.length !== 0 && (
          <div className='h-full px-5'>
            <ReportGraphLegend
              legends={reportsDataList.map((dataLabel) => dataLabel.label)}
              tooltipText={reportsDataList.map(
                (dataFullLabel) => dataFullLabel.fullLabel,
              )}
              colors={palette}
              align='left'
            />
            <ReportsBarGraph
              reportsDataList={reportsDataList}
              reportLabels={reportLabels}
              report={ReportFilters.TOTAL_REPORTS}
              palette={palette}
            />
          </div>
        )}
      {!isLoading &&
        !error &&
        reportsDataList.length === 0 &&
        reportLabels.length === 0 && (
          <NoDataFound
            message={intl.formatMessage({ defaultMessage: 'No data found' })}
          />
        )}
    </>
  );
}

const transformHandlingDataToBarChart = (
  dataFromApi: AverageHandlingTimeObject[],
) => {
  const legends = dataFromApi.map(
    (item) => item.case_manager_name ?? 'No Name',
  );

  const transformedData = legends.map((item) => {
    return {
      fullLabel: item,
      label: item,
      data: dataFromApi.map((subItem) => {
        return subItem.case_manager_name === item
          ? subItem.average_handling_time
          : 0;
      }),
      stack: 'total',
    };
  });

  return {
    data: transformedData,
    legend: legends,
  };
};

const AverageHandlingTime = ({
  filterBy,
  date,
  dateEnd,
  tenantId,
}: ReportByCaseManagerProps) => {
  const link = 'dashboard/average-handling-time';
  const [reportsDataList, setReportsDataList] = useState<BarChartObject[]>([]);
  const [reportLabels, setReportLabels] = useState<string[]>([]);
  const palette = ['#709FFF'];
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;

  const { data, error, isLoading } = useSWR(
    getUrlString(link, filterBy, date, dateEnd, tenantId, selectedChannel),
    {
      revalidateOnFocus: true,
    },
  );

  useEffect(() => {
    if (!isLoading && !error && data) {
      const reportData = transformHandlingDataToBarChart(data);
      setReportsDataList(reportData.data);
      setReportLabels(reportData.legend);
    }
  }, [data, error, isLoading]);

  return (
    <>
      {isLoading && <LoadingComponent />}
      {error && (
        <Typography
          sx={{
            fontSize: '14px',
            color: 'red',
            textAlign: 'center',
          }}
        >
          {error.message}
        </Typography>
      )}
      {!isLoading &&
        !error &&
        reportsDataList.length !== 0 &&
        reportLabels.length !== 0 && (
          <div className='h-full px-5'>
            <Typography
              sx={{
                color: '#878787',
                fontSize: 14,
                fontWeight: 400,
              }}
            >
              <FormattedMessage defaultMessage='Display the average handling time of resolved cases' />
            </Typography>
            <ReportsBarGraph
              reportsDataList={reportsDataList}
              reportLabels={reportLabels}
              report={ReportFilters.AVG_HANDLING_TIME}
              palette={palette}
            />
          </div>
        )}
      {!isLoading &&
        !error &&
        reportsDataList.length === 0 &&
        reportLabels.length === 0 && (
          <NoDataFound
            message={intl.formatMessage({ defaultMessage: 'No data found' })}
          />
        )}
    </>
  );
};

const ReportByCaseManager = () => {
  const intl = useIntl();
  const { selectedTenant } = usePartnerContext();
  const form = useDashoardFilter();

  const from = form.watch('date_range').from;
  const to = form.watch('date_range').to;

  const [filterReport, setFilterReport] = useState<ReportFilters>(
    ReportFilters.TOTAL_REPORTS,
  );

  const intlFormatFilterMessage = (value: ReportFilters) => {
    return value === ReportFilters.TOTAL_REPORTS
      ? intl.formatMessage({ defaultMessage: 'Total Reports' })
      : intl.formatMessage({ defaultMessage: 'Average Handling Time' });
  };

  return (
    <Card className='h-[440px] shadow-md'>
      <div className='flex justify-between'>
        <Typography sx={headerText}>
          <FormattedMessage defaultMessage='Case Managers' />
        </Typography>
        {/* Filter */}
        <Select
          value={filterReport}
          onChange={(_, value) => {
            if (value) {
              setFilterReport(value as ReportFilters);
            }
          }}
        >
          {Object.values(ReportFilters).map((value) => (
            <Option key={value} value={value}>
              {intlFormatFilterMessage(value)}
            </Option>
          ))}
        </Select>
      </div>
      {filterReport === ReportFilters.TOTAL_REPORTS ? (
        <CheckMetricWrapper
          metricCategory='Case Manager'
          metricName='Reports by Case Manager'
        >
          <TotalReports
            filterBy='day'
            date={from ? from.toISOString() : ''}
            dateEnd={to ? to.toISOString() : ''}
            tenantId={selectedTenant.id}
          />
        </CheckMetricWrapper>
      ) : (
        <CheckMetricWrapper
          metricCategory='Case Manager'
          metricName='Reports by Average Handling Time'
        >
          <AverageHandlingTime
            filterBy='day'
            date={from ? from.toISOString() : ''}
            dateEnd={to ? to.toISOString() : ''}
            tenantId={selectedTenant.id}
          />
        </CheckMetricWrapper>
      )}
    </Card>
  );
};

export default ReportByCaseManager;
